import { lazy, Suspense, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Code, CopyButton, Group, List, Stack, Text } from '@mantine/core';
import {
  getKalenderControllerStatusQueryKey,
  useKalenderControllerBeenden,
  useKalenderControllerErzeugen,
  useKalenderControllerStatus,
  type ApiError,
} from '@werkbank/shared';
import { formatDateTimeDE } from '../lib/format';
import { notify } from '../lib/notify';

const QRCodeSVG = lazy(() => import('qrcode.react').then((m) => ({ default: m.QRCodeSVG })));

/**
 * Kalender-Abo (Faruq 2026-10-30, Tobias/Kai Losin): die eigenen Plantafel-
 * Einträge im Kalender am Handy. Der Link wird nur beim Erzeugen angezeigt
 * (gespeichert ist nur ein Hash) — wer ihn verliert, erzeugt einen neuen; der
 * alte ist dann gesperrt.
 */
export function KalenderAboCard() {
  const qc = useQueryClient();
  const status = useKalenderControllerStatus();
  const [link, setLink] = useState<string | null>(null);
  const auffrischen = () =>
    void qc.invalidateQueries({ queryKey: getKalenderControllerStatusQueryKey() });
  const erzeugen = useKalenderControllerErzeugen<ApiError>({
    mutation: {
      onSuccess: (r) => {
        setLink(r.link);
        auffrischen();
      },
      onError: (e) => notify.error(e.message, 'Kalender-Abo'),
    },
  });
  const beenden = useKalenderControllerBeenden<ApiError>({
    mutation: {
      onSuccess: () => {
        setLink(null);
        auffrischen();
        notify.success('Kalender-Abo beendet — der Link funktioniert nicht mehr.');
      },
    },
  });
  const aktivSeit = status.data?.aktivSeit ?? null;

  return (
    <Card padding={24} data-testid="kalender-abo">
      <Stack gap="md">
        <Text fw={600} style={{ fontSize: 'var(--wb-font-size-lg)' }}>
          Kalender-Abo (Handy)
        </Text>
        <Text size="sm" c="dimmed">
          Deine Plantafel-Einträge — Einsätze, Bereitschaft, interne Blöcke und Abwesenheiten — in
          der Kalender-App am Handy. Nur lesend; geändert wird weiter in der Werkbank.
        </Text>
        <Text size="sm" data-testid="kalender-abo-status">
          {aktivSeit ? `Abo aktiv seit ${formatDateTimeDE(aktivSeit)}.` : 'Kein Abo eingerichtet.'}
        </Text>

        {link && (
          <>
            <Alert variant="light" color="blue">
              Der Link wird nur jetzt angezeigt. Er ist persönlich — wer ihn hat, sieht deine
              Termine. Bei Verlust einfach einen neuen erzeugen.
            </Alert>
            <Group gap="xs" wrap="nowrap">
              <Code style={{ wordBreak: 'break-all' }} data-testid="kalender-abo-link">
                {link}
              </Code>
              <CopyButton value={link}>
                {({ copied, copy }) => (
                  <Button size="compact-sm" variant="light" onClick={copy}>
                    {copied ? 'Kopiert' : 'Kopieren'}
                  </Button>
                )}
              </CopyButton>
            </Group>
            {/* Weißes Feld über den Mantine-Token — der QR bleibt im Dunkelmodus scanbar. */}
            <Group justify="center">
              <div
                style={{ background: 'var(--mantine-color-white)', padding: 12, borderRadius: 8 }}
              >
                <Suspense fallback={<div style={{ width: 180, height: 180 }} />}>
                  <QRCodeSVG value={link} size={180} />
                </Suspense>
              </div>
            </Group>
            <Text size="sm" fw={600}>
              So kommt es aufs Android-Handy:
            </Text>
            <List size="sm" spacing={4}>
              <List.Item>App „ICSx⁵" aus dem Play Store installieren (kostenlos).</List.Item>
              <List.Item>
                In ICSx⁵ auf „+" tippen, den Link einfügen (oder den QR-Code mit der Kamera scannen
                und den Link kopieren) und „Synchronisierung alle 15 Minuten" wählen.
              </List.Item>
              <List.Item>
                Fertig — der Kalender „Werkbank" erscheint in jeder Kalender-App des Handys.
              </List.Item>
            </List>
          </>
        )}

        <Group>
          <Button
            variant={aktivSeit ? 'default' : 'filled'}
            loading={erzeugen.isPending}
            onClick={() => erzeugen.mutate()}
            data-testid="kalender-abo-erzeugen"
          >
            {aktivSeit ? 'Neuen Link erzeugen (alter wird gesperrt)' : 'Abo-Link erzeugen'}
          </Button>
          {aktivSeit && (
            <Button
              variant="subtle"
              color="red"
              loading={beenden.isPending}
              onClick={() => beenden.mutate()}
              data-testid="kalender-abo-beenden"
            >
              Abo beenden
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
